import type { AdminFoodDetail, AdminFoodListItem } from '@steado/admin-contract/foods';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';
import { useDebounced } from '@/lib/use-debounced';
import { cn } from '@/lib/utils';

import { foodsQueryOptions, mergeFood } from '@/screens/foods/foods-query';

interface MergeDialogProps {
  readonly food: AdminFoodDetail;
  readonly onMerged: (survivor: AdminFoodDetail) => void;
  readonly onCancel: () => void;
}

/**
 * Picks the row that survives, then folds this one into it.
 *
 * The survivor is chosen by searching the catalog rather than typed as an id:
 * an operator merging two "Rajma masala" rows is looking at names, and asking
 * them to copy a UUID is how the wrong one gets merged.
 */
export function MergeDialog({ food, onMerged, onCancel }: MergeDialogProps) {
  const { api } = useAuth();
  const [term, setTerm] = useState('');
  const [target, setTarget] = useState<AdminFoodListItem | null>(null);
  const debounced = useDebounced(term, 260);
  const searchId = useId();

  const candidates = useQuery({
    ...foodsQueryOptions(api, { search: debounced, activity: 'active' }),
    enabled: debounced.length >= 2,
  });

  const merge = useMutation({
    mutationFn: () => mergeFood(api, food.id, target?.id ?? ''),
    onSuccess: onMerged,
    retry: false,
  });

  // The row being merged away is never a candidate to merge into: the API
  // refuses it, and offering it is an invitation to a 422.
  const options = (candidates.data?.items ?? []).filter((item) => item.id !== food.id);

  return (
    <ConfirmDialog
      title={`Merge ${food.name} into…`}
      body={
        <div className="flex flex-col gap-4">
          <p>
            {food.name} is deactivated and its aliases, including its own name, move to the food you pick.
            Meal logs and scans stay where they are: their macros were captured when they were logged.
          </p>

          <div className="flex flex-col gap-2">
            <Label htmlFor={searchId}>Search for the food that survives</Label>
            <Input
              id={searchId}
              type="search"
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setTarget(null);
              }}
            />
          </div>

          {debounced.length < 2 ? null : candidates.isPending ? (
            <p className="text-[13px] text-muted-foreground">Searching…</p>
          ) : options.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">No other active food matches that.</p>
          ) : (
            <ul className="max-h-[200px] overflow-y-auto rounded-md border border-border">
              {options.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    aria-pressed={target?.id === option.id}
                    onClick={() => {
                      setTarget(option);
                    }}
                    className={cn(
                      'flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-accent',
                      target?.id === option.id ? 'bg-primary/10 font-medium text-primary' : 'text-foreground',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{option.name}</span>
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">{option.source}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {target === null ? null : (
            <p className="text-sm text-foreground">
              Merging into <span className="font-medium">{target.name}</span>.
            </p>
          )}
        </div>
      }
      confirmLabel="Merge"
      isPending={merge.isPending}
      {...(merge.isError ? { error: merge.error.message } : {})}
      // Shut until a survivor is picked. Not `typeToConfirm` — this is a
      // missing input, not a speed bump.
      confirmDisabled={target === null}
      onConfirm={() => {
        if (target !== null) merge.mutate();
      }}
      onCancel={onCancel}
    />
  );
}
